import { forwardRef, type InputHTMLAttributes, memo, type ReactNode, useCallback, useState } from "react";
import { cn } from "src/utilities/shadcnUtils";

import { LightText } from "../v1/Text";

// beware: children has a higher priority to label
export interface CheckboxProps
  extends Omit<
    React.DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "onChange" | "style"
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  wrapperClassName?: string;
  labelClassName?: string;
  label?: string;
  name?: string;
  htmlFor?: string;
  large?: boolean;
  children?: ReactNode;
  className?: string;
}

const STYLES = {
  container: "flex flex-row items-center cursor-pointer",
  largeContainer: "items-start",
  label: "max-w-[345px] text-sm text-left text-gray-900 tracking-[0.01em] leading-[145%]",
  checkbox:
    "h-6 w-6 min-w-6 rounded max-[420px]:h-6 max-[420px]:w-6 max-[420px]:min-w-6 max-[420px]:rounded flex justify-center items-center border border-solid border-gray-400 mr-5",
  checkboxFiller:
    "h-4 w-4 min-w-4 rounded-[2px] max-[420px]:h-4 max-[420px]:w-4 max-[420px]:min-w-4 max-[420px]:rounded-[2px] bg-green-500",
  largeCheckbox: "h-12 w-12 min-w-12 rounded-lg",
  largeCheckboxFiller: "h-8 w-8 rounded",
  hideNativeCheckbox:
    "[clip:rect(0_0_0_0)] [clip-path:inset(50%)] h-px overflow-hidden whitespace-nowrap fixed w-px",
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onChange,
      wrapperClassName,
      label,
      name,
      htmlFor,
      labelClassName,
      large,
      children,
      className,
    }: CheckboxProps,
    forwardedRef
  ) => {
    const [innerCheckedState, setInnerCheckedState] = useState(checked || defaultChecked);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event.target.checked);
        if (checked === undefined) {
          setInnerCheckedState(event.target.checked);
        }
      },
      [onChange, checked]
    );

    return (
      <label htmlFor={htmlFor}>
        <input
          className={cn(STYLES.hideNativeCheckbox, className)}
          id={htmlFor}
          name={name}
          type="checkbox"
          ref={forwardedRef}
          checked={checked !== undefined ? checked : undefined}
          defaultChecked={defaultChecked}
          onChange={handleChange}
        />
        <div className={wrapperClassName}>
          <div className={cn(STYLES.container, large && STYLES.largeContainer)} aria-hidden="true">
            <div className={cn(STYLES.checkbox, large && STYLES.largeCheckbox)}>
              <div
                className={cn(
                  STYLES.checkboxFiller,
                  large && STYLES.largeCheckboxFiller,
                  "transition-transform duration-200",
                  (checked ?? innerCheckedState) ? "scale-100" : "scale-0"
                )}
              />
            </div>
            {children || (
              <LightText paragraph className={cn(STYLES.label, labelClassName)}>
                {label}
              </LightText>
            )}
          </div>
        </div>
      </label>
    );
  }
);

export default memo(Checkbox);
